import {ContactRelationRepo} from "../api/contact-relation.repo";
import {inject, Injectable} from "@angular/core";
import {OrgDto} from "../dto/org.dto";
import {SearchRequest} from "../dto/search/search-table.dto.req";
import {forkJoin, Observable} from "rxjs";
import {WrapperDto} from "../dto/wrapper.dto";
import {SearchContactRelationRes} from "../dto/search/search-contact-relation.res";
import {ContactRelationDto} from "../dto/contact.relation.dto";
import {map} from "rxjs/operators";
import {DuckDbHelper, sqlDirection, sqlLike, sqlNumber, sqlText} from "./duckdb.helper";
import {isDefined, nonBlank} from "../../libs/utils/epic.utils";
import {SearchFilterResponse} from "../dto/search/search-table.dto.res";
import {LoggingService} from "../../services/logger/logging.service";

@Injectable({providedIn: 'root'})
export class ContactRelationDuckdb extends ContactRelationRepo {

  private readonly duck = inject(DuckDbHelper);
  private readonly log = inject(LoggingService);

  public override getById(_org: OrgDto, relationId: string, parentContactId: string): Observable<WrapperDto<ContactRelationDto>> {
    const selectRelation = `
      SELECT r.id                                                        AS id,
             r.type                                                      AS type,
             r.label                                                     AS label,
             r.parent_id                                                 AS parentId,
             r.child_id                                                  AS childId,
             b.type                                                      AS bidirectionalType,
             r.bidirectional_id                                          AS bidirectionalId,
             c.type                                                      AS childType,
             c.number                                                    AS childNumber,
             CONCAT_WS(' ', NULLIF(c.firstname, ''), NULLIF(c.name, '')) AS childFullName
      FROM contact_relation r
             LEFT JOIN contact_relation b ON b.id = r.bidirectional_id
             LEFT JOIN contact c ON c.id = r.child_id
      WHERE r.id = ${sqlText(relationId)}
        AND r.parent_id = ${sqlText(parentContactId)}`;
    return forkJoin([
      this.duck.sql(selectRelation),
    ]).pipe(map(([rel]) => {
      if (DuckDbHelper.hasRes(rel)) {
        return DuckDbHelper.data('ContactDto', new ContactRelationDto(rel[0]));
      }
      return DuckDbHelper.data('ContactDto', new ContactRelationDto());
    }));
  }

  public override search(_org: OrgDto, request: SearchRequest): Observable<WrapperDto<SearchContactRelationRes>> {
    const limit = request.pageSize;
    const offset = request.pageSize * (request.page - 1);
    const sort = this.buildSort(request);
    const query = this.buildQuery(request);
    const filter = this.buildFilter(request);
    const search = `
      SELECT r.id                                                        AS id,
             r.type                                                      AS type,
             r.parent_id                                                 AS parentId,
             r.child_id                                                  AS childId,
             b.type                                                      AS bidirectionalType,
             r.bidirectional_id                                          AS bidirectionalId,
             c.type                                                      AS childType,
             c.number                                                    AS childNumber,
             CONCAT_WS(' ', NULLIF(c.firstname, ''), NULLIF(c.name, '')) AS childFullName,
             COUNT(*)                                                       OVER (ROWS BETWEEN UNBOUNDED PRECEDING AND UNBOUNDED FOLLOWING)               AS total_results
      FROM contact_relation r
             LEFT JOIN contact_relation b ON b.id = r.bidirectional_id
             LEFT JOIN contact c ON c.id = r.child_id
      WHERE ${isDefined(query) ? query : '1=1'}
        AND ${isDefined(filter) ? filter : '1=1'}
      ORDER BY ${sort} LIMIT ${sqlNumber(limit, 20)}
      OFFSET ${sqlNumber(offset, 0)}`;
    return this.duck.sql(search).pipe(map((res) => {
      const hasRes = isDefined(res) && res.length > 0;
      const firstRow = hasRes ? res[0] : {total_results: 0} as any;
      const result = new SearchContactRelationRes({
        items: hasRes ? res : [],
        hits: firstRow.total_results,
        page: request.page,
        pageSize: request.pageSize,
        sortField: request.sortField,
        sortOrder: request.sortOrder,
        filters: request.filters.map((f) => new SearchFilterResponse(f))
      });
      return DuckDbHelper.data('SearchContactRelationRes', result);
    }));
  }

  private buildSort(request: SearchRequest) {
    let field;
    if (isDefined(request.sortField)) {
      switch (request.sortField) {
        case '':
          field = 'c.type';
          break;
        case 'type':
          field = 'c.type';
          break;
        case 'childNumber':
          field = 'c.number';
          break;
        case 'childFullName':
          field = 'CONCAT_WS(\' \', NULLIF(c.firstname, \'\'), NULLIF(c.name, \'\'))';
          break;
        default:
          this.log.warn('Unhandled sort field "{}"', [request.sortField]);
          field = 'c.type';
      }
    } else {
      field = 'c.type';
    }
    return field + ' ' + sqlDirection(request.sortOrder);
  }

  private buildQuery(request: SearchRequest): string | undefined {
    if (isDefined(request.query) && nonBlank(request.query)) {
      return `(c.number ILIKE ${sqlLike(request.query)}
          OR c.firstname ILIKE ${sqlLike(request.query)}
          OR c.name ILIKE ${sqlLike(request.query)})`;
    }
    return undefined;
  }

  private buildFilter(request: SearchRequest): string | undefined {
    const filters: string[] = [];
    // Kontextfilter aus der Route: Bei leerer Auswahl ist "nichts" richtig - "alles" waere die
    // Beziehungsliste fremder Kontakte am eigenen Reiter. Siehe sqlText() zum blanken Token NULL.
    const paramContactId = request.filters.find((f: any) => f.id === 'param-contact-id');
    if (isDefined(paramContactId) && isDefined(paramContactId.selected)) {
      filters.push(paramContactId.selected.length > 0 ? `r.parent_id  = ${sqlText(paramContactId.selected[0])}` : '1=0');
    }
    return filters.length > 0 ? filters.join(' AND ') : undefined;
  }
}
